/**
 * DOM 側の HUD 管理。
 *
 * ゲーム中に常時出るのはクロスヘアとホットバーだけ。タッチ端末ではホットバーの代わりにスティックとジャンプ。
 * 設定類は Esc メニュー、操作方法はメニューから開くダイアログ、ホットバー編集は E インベントリ、描画設定は V グラフィックメニューに分けている。
 * 閲覧モードではメニューだけ閉じてワールドを見せ、入力は受けない。
 * three.js 側の状態は一切持たず、渡された値を描くだけ。
 */

import {
  BLOCK_TEXTURE_PATTERNS,
  MAX_TEXTURE_LIST_LENGTH,
  UNCATEGORIZED_TEXTURE_LIST,
  type BlockTextureDefinition,
  type BlockTexturePattern,
  type BlockTextureResolution,
} from '../shared/block-textures';
import type { PeerInfo } from '../shared/types';
import {
  TEXTURE_PACKS,
  materialPreviewUrl,
  resolveMaterial,
} from '../shared/materials';
import { blockTextureDataUrl, paintBlockTexture } from './block-texture-generator';
import { OBJECT_DEFINITIONS } from '../shared/object-catalog';
import {
  BUILD_SHAPES,
  EMPTY_SLOT_INDEX,
  HOTBAR_SIZE,
  createBuildSlot,
  createBraceBendToolSlot,
  createCopyPasteToolSlot,
  createDestroyToolSlot,
  createEmptySlot,
  createExtrudeDownToolSlot,
  createExtrudeToolSlot,
  createExtrudeUpToolSlot,
  createClothSimToolSlot,
  createHoleToolSlot,
  createRevolveToolSlot,
  createLightSlot,
  createPlaceableSlot,
  createRangeDestroyToolSlot,
  createWaterSlot,
  toolSlotLabel,
  type Builder,
  type SlotConfig,
  type ToolId,
} from './build';
import type { ConnectionStatus } from './connection';
import { prefersTouchPlay } from './touch-play';
import type { GraphicsQualityId } from './graphics-quality';
import { GRAPHICS_QUALITY_IDS } from './graphics-quality';
import {
  isRestirDenoiserMode,
  type RestirDenoiserMode,
} from './restir-denoiser';
import {
  DENOISER_MODE_PATHS,
  LOCALES,
  LOCALE_LABELS,
  formatLocaleNumber,
  getLocale,
  materialLabel,
  objectLabel,
  packLabel,
  setLocale,
  subscribeLocale,
  t,
  translate,
  messageCatalog,
  type MessagePath,
} from './i18n';

/** メニュー / 操作方法 / インベントリ / グラフィック / グリッド編集 / 閲覧（カーソル自由・操作なし） / プレイ */
export type UiMode = 'menu' | 'controls' | 'inventory' | 'graphics' | 'grid-editor' | 'cloth-sim' | 'viewing' | 'playing';

type InventoryTabId = 'brace' | 'plane' | 'polygon' | 'stair' | 'object' | 'tool' | 'texture';

const INVENTORY_TAB_IDS: InventoryTabId[] = [
  'plane',
  'polygon',
  'stair',
  'object',
  'brace',
  'tool',
  'texture',
];

function isInventoryTabId(value: string | undefined): value is InventoryTabId {
  return value !== undefined && (INVENTORY_TAB_IDS as string[]).includes(value);
}

export interface HudHandlers {
  onPlay(): void;
  onView(): void;
  onGraphics(): void;
  onControls(): void;
  onModeChange(mode: UiMode): void;
  onRoomChange(room: string): void;
  onNameChange(name: string): void;
  onSensitivityChange(value: number): void;
  onInvertHotbarScrollChange(enabled: boolean): void;
  onGraphicsQualityChange(id: GraphicsQualityId): void;
  onDepthOfFieldChange(enabled: boolean): void;
  onSunSdfShadowsChange(enabled: boolean): void;
  onDiSdfShadowsChange(enabled: boolean): void;
  onGiSdfShadowsChange(enabled: boolean): void;
  onSdfAnalyticChange(enabled: boolean): void;
  onSdfAnalyticBvhChange(enabled: boolean): void;
  onSdfVoxelSpheresChange(enabled: boolean): void;
  onSunlightChange(enabled: boolean): void;
  onAmbientChange(enabled: boolean): void;
  onGiDiffuseGainChange(value: number): void;
  onGiDistanceAmbientChange(value: number): void;
  onGiNearAmbientChange(value: number): void;
  onDenoiserChange(mode: RestirDenoiserMode): void;
  onRestirCheapVisibilityChange(enabled: boolean): void;
  onCollisionDebugChange(enabled: boolean): void;
  onGraphicsReset(): void;
  onSlotChange(index: number, config: SlotConfig): void;
  onTextureCreate(texture: BlockTextureDefinition, slotIndex: number): boolean;
}

function el<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) throw new Error(`Missing element: #${id}`);
  return found as T;
}

const CONTROL_KEYS: MessagePath[] = [
  'control.touch',
  'control.move',
  'control.fly',
  'control.sprint',
  'control.click',
  'control.hotbar',
  'control.destroy',
  'control.inventory',
  'control.graphics',
  'control.brace',
  'control.plane',
  'control.polygon',
  'control.stair',
  'control.thickness',
  'control.extra',
  'control.menu',
];

const INVENTORY_TAB_KEYS: Record<InventoryTabId, MessagePath> = {
  brace: 'inventory.tabBrace',
  plane: 'inventory.tabPlane',
  polygon: 'inventory.tabPolygon',
  stair: 'inventory.tabStair',
  object: 'inventory.tabObject',
  tool: 'inventory.tabTool',
  texture: 'inventory.tabTexture',
};

const SHAPE_GLYPHS = {
  brace: '╱',
  plane: '▱',
  polygon: '★',
  stair: '▤',
  object: '▢',
} as const;

const TOOL_GLYPHS: Record<ToolId, string> = {
  destroy: '✕',
  'range-destroy': '▣',
  'copy-paste': '⧉',
  'brace-bend': '⌒',
  extrude: '⬆',
  'extrude-up': '↥',
  'extrude-down': '↧',
  hole: '◯',
  'cloth-sim': '∿',
  revolve: '⟳',
};
const TOOL_SWATCH_COLORS: Record<ToolId, number> = {
  destroy: 0x4a4a4a,
  'range-destroy': 0x6a3a3a,
  'copy-paste': 0x3a5a7a,
  'brace-bend': 0x3a5a6a,
  extrude: 0x3a6a4a,
  'extrude-up': 0x2e7a4a,
  'extrude-down': 0x2e5a7a,
  hole: 0x3a4a6a,
  'cloth-sim': 0x6a4a5a,
  revolve: 0x5a4a7a,
};
const NEW_TEXTURE_LIST_VALUE = '__new__';

export class Hud {
  private readonly gameUi = el<HTMLDivElement>('game-ui');
  private readonly menu = el<HTMLDivElement>('menu');
  private readonly viewHint = el<HTMLDivElement>('view-hint');
  private readonly menuStatus = el<HTMLParagraphElement>('menu-status');
  private readonly menuBoot = el<HTMLParagraphElement>('menu-boot');
  private readonly hotbar = el<HTMLDivElement>('hotbar');
  private readonly itemName = el<HTMLDivElement>('item-name');
  private readonly shapeLine = el<HTMLDivElement>('shape-line');
  private readonly fps = el<HTMLDivElement>('fps');
  private readonly debug = el<HTMLPreElement>('debug');
  private readonly playerlist = el<HTMLDivElement>('playerlist');
  private readonly playerlistRoom = el<HTMLSpanElement>('playerlist-room');
  private readonly playerlistItems = el<HTMLUListElement>('playerlist-items');
  private readonly menuPlayersHeading = el<HTMLHeadingElement>('menu-players-heading');
  private readonly menuPlayerlistItems = el<HTMLUListElement>('menu-playerlist-items');
  private readonly menuPlayersEmpty = el<HTMLParagraphElement>('menu-players-empty');
  private readonly toasts = el<HTMLDivElement>('toasts');
  private readonly roomInput = el<HTMLInputElement>('room-input');
  private readonly nameInput = el<HTMLInputElement>('name-input');
  private readonly sensitivity = el<HTMLInputElement>('sensitivity');
  private readonly sensitivityValue = el<HTMLOutputElement>('sensitivity-value');
  private readonly graphicsQuality = el<HTMLSelectElement>('graphics-quality');
  private readonly depthOfField = el<HTMLInputElement>('depth-of-field');
  private readonly sunSdfShadows = el<HTMLInputElement>('sdf-sun-shadows');
  private readonly diSdfShadows = el<HTMLInputElement>('sdf-di-shadows');
  private readonly giSdfShadows = el<HTMLInputElement>('sdf-gi-shadows');
  private readonly sdfAnalytic = el<HTMLInputElement>('sdf-analytic');
  private readonly sdfAnalyticBvh = el<HTMLInputElement>('sdf-analytic-bvh');
  private readonly sdfVoxelSpheres = el<HTMLInputElement>('sdf-voxel-spheres');
  private readonly sunlight = el<HTMLInputElement>('sunlight');
  private readonly ambientLight = el<HTMLInputElement>('ambient-light');
  private readonly giDiffuseGain = el<HTMLInputElement>('gi-diffuse-gain');
  private readonly giDiffuseGainValue = el<HTMLOutputElement>('gi-diffuse-gain-value');
  private readonly giDistanceAmbient = el<HTMLInputElement>('gi-distance-ambient');
  private readonly giDistanceAmbientValue = el<HTMLOutputElement>('gi-distance-ambient-value');
  private readonly giNearAmbient = el<HTMLInputElement>('gi-near-ambient');
  private readonly giNearAmbientValue = el<HTMLOutputElement>('gi-near-ambient-value');
  private readonly denoiser = el<HTMLSelectElement>('restir-denoiser');
  private readonly restirCheapVisibility = el<HTMLInputElement>('restir-cheap-visibility');
  private readonly collisionDebug = el<HTMLInputElement>('collision-debug');
  private readonly localeSelect = el<HTMLSelectElement>('locale-select');
  private readonly graphicsMenu = el<HTMLDivElement>('graphics-menu');
  private readonly controlsMenu = el<HTMLDivElement>('controls-menu');
  private readonly inventory = el<HTMLDivElement>('inventory');
  private readonly inventoryHotbar = el<HTMLDivElement>('inventory-hotbar');
  private readonly invertHotbarScroll = el<HTMLInputElement>('invert-hotbar-scroll');
  private readonly inventoryInspectorTitle = el<HTMLHeadingElement>('inventory-inspector-title');
  private readonly inventorySlotDetails = el<HTMLDivElement>('inventory-slot-details');
  private readonly inventoryTabs = Array.from(
    document.querySelectorAll<HTMLButtonElement>('[data-inventory-tab].inventory-tab')
  );
  private readonly inventoryTabPanels = Array.from(
    document.querySelectorAll<HTMLElement>('.inventory-tab-panel[data-inventory-tab]')
  );
  private readonly inventoryCatalogBrace = el<HTMLDivElement>('inventory-catalog-brace');
  private readonly inventoryCatalogPlane = el<HTMLDivElement>('inventory-catalog-plane');
  private readonly inventoryCatalogPolygon = el<HTMLDivElement>('inventory-catalog-polygon');
  private readonly inventoryCatalogStair = el<HTMLDivElement>('inventory-catalog-stair');
  private readonly inventoryCatalogObject = el<HTMLDivElement>('inventory-catalog-object');
  private readonly inventoryCatalogTool = el<HTMLDivElement>('inventory-catalog-tool');
  private readonly textureCreator = el<HTMLFormElement>('texture-creator');
  private readonly textureName = el<HTMLInputElement>('texture-name');
  private readonly textureList = el<HTMLSelectElement>('texture-list');
  private readonly textureListNewField = el<HTMLLabelElement>('texture-list-new-field');
  private readonly textureListNew = el<HTMLInputElement>('texture-list-new');
  private readonly textureColor = el<HTMLInputElement>('texture-color');
  private readonly texturePattern = el<HTMLSelectElement>('texture-pattern');
  private readonly textureResolution = el<HTMLSelectElement>('texture-resolution');
  private readonly textureNoiseSize = el<HTMLInputElement>('texture-noise-size');
  private readonly textureNoiseSizeValue = el<HTMLOutputElement>('texture-noise-size-value');
  private readonly textureNoiseContrast = el<HTMLInputElement>('texture-noise-contrast');
  private readonly textureNoiseContrastValue = el<HTMLOutputElement>(
    'texture-noise-contrast-value'
  );
  private readonly texturePreview = el<HTMLCanvasElement>('texture-preview');

  private readonly slots: HTMLDivElement[] = [];
  private readonly inventorySlots: HTMLButtonElement[] = [];
  private itemNameTimer = 0;
  private mode: UiMode = 'menu';
  private editingSlot = 1;
  private activeInventoryTab: InventoryTabId = 'plane';
  private builder: Builder | null = null;
  private draggedItem: SlotConfig | null = null;
  private textureSeed = randomTextureSeed();
  private playerName = 'anonymous';
  private readonly blockTextures = new Map<string, BlockTextureDefinition>();
  private readonly texturePreviewUrls = new Map<string, string>();
  private readonly sessionTextureLists = new Set<string>();
  private status: ConnectionStatus = 'connecting';
  private lastSelf: PeerInfo | null = null;
  private lastPeers: PeerInfo[] = [];

  constructor(private readonly handlers: HudHandlers) {
    this.buildHotbar();
    this.buildInventory();
    this.wireInventoryTabs();
    this.wireTextureCreator();
    this.wireMenu();
    this.applyCopy();
    subscribeLocale(() => this.applyCopy());
  }

  get uiMode(): UiMode {
    return this.mode;
  }

  /* ---------------------------------------------------------------- */
  /* 組み立て                                                          */
  /* ---------------------------------------------------------------- */

  private buildHotbar(): void {
    const loadout = document.createElement('div');
    loadout.className = 'hotbar-loadout';
    for (let i = 0; i < HOTBAR_SIZE; i++) {
      const slot = document.createElement('div');
      slot.className = 'slot';
      const key = document.createElement('span');
      key.className = 'key';
      key.textContent = String(i);
      const shape = document.createElement('span');
      shape.className = 'slot-shape';
      slot.append(shape, key);
      if (i === EMPTY_SLOT_INDEX) {
        const frame = document.createElement('div');
        frame.className = 'hotbar-hand';
        frame.append(slot);
        this.hotbar.append(frame);
      } else {
        loadout.append(slot);
      }
      this.slots.push(slot);
    }
    this.hotbar.append(loadout);
  }

  private wireMenu(): void {
    el<HTMLButtonElement>('menu-play').addEventListener('click', () => this.handlers.onPlay());
    el<HTMLButtonElement>('menu-view').addEventListener('click', () => this.handlers.onView());
    el<HTMLButtonElement>('menu-graphics').addEventListener('click', () =>
      this.handlers.onGraphics()
    );
    el<HTMLButtonElement>('menu-controls').addEventListener('click', () =>
      this.handlers.onControls()
    );

    el<HTMLButtonElement>('room-join').addEventListener('click', () => {
      this.handlers.onRoomChange(this.roomInput.value.trim());
    });
    this.roomInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') this.handlers.onRoomChange(this.roomInput.value.trim());
    });

    el<HTMLButtonElement>('name-apply').addEventListener('click', () => {
      this.handlers.onNameChange(this.nameInput.value.trim());
    });
    this.nameInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') this.handlers.onNameChange(this.nameInput.value.trim());
    });

    this.sensitivity.addEventListener('input', () => {
      const value = Number(this.sensitivity.value);
      this.sensitivityValue.textContent = value.toFixed(2);
      this.handlers.onSensitivityChange(value);
    });

    this.invertHotbarScroll.addEventListener('change', () => {
      this.handlers.onInvertHotbarScrollChange(this.invertHotbarScroll.checked);
    });

    this.graphicsQuality.addEventListener('change', () => {
      const id = this.graphicsQuality.value as GraphicsQualityId;
      if (!GRAPHICS_QUALITY_IDS.includes(id)) return;
      this.handlers.onGraphicsQualityChange(id);
    });

    this.depthOfField.addEventListener('change', () => {
      this.handlers.onDepthOfFieldChange(this.depthOfField.checked);
    });

    this.sunSdfShadows.addEventListener('change', () => {
      this.handlers.onSunSdfShadowsChange(this.sunSdfShadows.checked);
    });
    this.diSdfShadows.addEventListener('change', () => {
      this.handlers.onDiSdfShadowsChange(this.diSdfShadows.checked);
    });
    this.giSdfShadows.addEventListener('change', () => {
      this.handlers.onGiSdfShadowsChange(this.giSdfShadows.checked);
    });
    this.sdfAnalytic.addEventListener('change', () => {
      this.handlers.onSdfAnalyticChange(this.sdfAnalytic.checked);
    });
    this.sdfAnalyticBvh.addEventListener('change', () => {
      this.handlers.onSdfAnalyticBvhChange(this.sdfAnalyticBvh.checked);
    });
    this.sdfVoxelSpheres.addEventListener('change', () => {
      this.handlers.onSdfVoxelSpheresChange(this.sdfVoxelSpheres.checked);
    });

    this.sunlight.addEventListener('change', () => {
      this.handlers.onSunlightChange(this.sunlight.checked);
    });

    this.ambientLight.addEventListener('change', () => {
      this.handlers.onAmbientChange(this.ambientLight.checked);
    });

    this.giDiffuseGain.addEventListener('input', () => {
      const value = Number(this.giDiffuseGain.value);
      this.giDiffuseGainValue.textContent = value.toFixed(1);
      this.handlers.onGiDiffuseGainChange(value);
    });
    this.giDistanceAmbient.addEventListener('input', () => {
      const value = Number(this.giDistanceAmbient.value);
      this.giDistanceAmbientValue.textContent = value.toFixed(2);
      this.handlers.onGiDistanceAmbientChange(value);
    });
    this.giNearAmbient.addEventListener('input', () => {
      const value = Number(this.giNearAmbient.value);
      this.giNearAmbientValue.textContent = value.toFixed(2);
      this.handlers.onGiNearAmbientChange(value);
    });

    this.denoiser.addEventListener('change', () => {
      if (!isRestirDenoiserMode(this.denoiser.value)) return;
      this.handlers.onDenoiserChange(this.denoiser.value);
    });

    this.restirCheapVisibility.addEventListener('change', () => {
      this.handlers.onRestirCheapVisibilityChange(this.restirCheapVisibility.checked);
    });

    this.collisionDebug.addEventListener('change', () => {
      this.handlers.onCollisionDebugChange(this.collisionDebug.checked);
    });

    el<HTMLButtonElement>('graphics-reset').addEventListener('click', () =>
      this.handlers.onGraphicsReset()
    );

    this.localeSelect.addEventListener('change', () => {
      const locale = this.localeSelect.value;
      if (locale !== 'ja' && locale !== 'en') return;
      setLocale(locale);
    });
  }

  applyCopy(): void {
    document.title = t('meta.title');
    setText('menu-play', t('menu.play'));
    setText('menu-view', t('menu.view'));
    setText('menu-graphics', t('menu.graphicsMenu'));
    setText('menu-controls', t('menu.controlsMenu'));
    setText('menu-room-label', t('menu.room'));
    setText('room-join', t('menu.join'));
    setText('menu-name-label', t('menu.playerName'));
    setText('name-apply', t('menu.change'));
    setText('menu-sensitivity-label', t('menu.sensitivity'));
    setText('menu-graphics-label', t('menu.graphics'));
    setText('menu-dof-label', t('menu.depthOfField'));
    setText('menu-sdf-sun-shadows-label', t('menu.sunSdfShadows'));
    setText('menu-sdf-di-shadows-label', t('menu.diSdfShadows'));
    setText('menu-sdf-gi-shadows-label', t('menu.giSdfShadows'));
    setText('menu-sdf-analytic-label', t('menu.sdfAnalytic'));
    setText('menu-sdf-analytic-bvh-label', t('menu.sdfAnalyticBvh'));
    setText('menu-sdf-voxel-spheres-label', t('menu.sdfVoxelSpheres'));
    setText('menu-sunlight-label', t('menu.sunlight'));
    setText('menu-ambient-label', t('menu.ambient'));
    setText('menu-gi-diffuse-gain-label', t('menu.giDiffuseGain'));
    setText('menu-gi-distance-ambient-label', t('menu.giDistanceAmbient'));
    setText('menu-gi-near-ambient-label', t('menu.giNearAmbient'));
    setText('menu-denoiser-label', t('menu.denoiser'));
    setText('menu-restir-cheap-visibility-label', t('menu.restirCheapVisibility'));
    setText('menu-collision-debug-label', t('menu.collisionDebug'));
    setText('menu-language-label', t('menu.language'));
    setText('menu-credits-title', t('menu.credits'));
    setText('menu-texture-credit', t('menu.textureCredit'));
    setText('playerlist-heading', t('playerlist.room'));
    this.viewHint.textContent = t('viewHint');
    setText('mobile-jump', t('mobile.jump'));
    el<HTMLElement>('mobile-stick').setAttribute('aria-label', t('mobile.moveAria'));
    el<HTMLElement>('mobile-jump').setAttribute('aria-label', t('mobile.jump'));

    for (const option of this.graphicsQuality.options) {
      if (option.value === 'low' || option.value === 'medium' || option.value === 'high') {
        option.textContent = t(
          option.value === 'low'
            ? 'graphics.low'
            : option.value === 'medium'
              ? 'graphics.medium'
              : 'graphics.high'
        );
      }
    }

    for (const option of this.denoiser.options) {
      if (!isRestirDenoiserMode(option.value)) continue;
      option.textContent = t(DENOISER_MODE_PATHS[option.value]);
    }

    this.fillLocaleSelect();
    this.fillControls();
    this.fillControlsChrome();
    this.fillGraphicsChrome();
    this.fillInventoryChrome();
    this.fillTextureCreatorCopy();
    this.renderInventoryCatalogs();
    this.setStatus(this.status);
    if (this.builder) this.renderBuilder(this.builder);
    this.renderPlayers(this.lastSelf, this.lastPeers);
  }

  private fillLocaleSelect(): void {
    const current = getLocale();
    if (this.localeSelect.options.length !== LOCALES.length) {
      this.localeSelect.replaceChildren();
      for (const locale of LOCALES) {
        const option = document.createElement('option');
        option.value = locale;
        option.textContent = LOCALE_LABELS[locale];
        this.localeSelect.append(option);
      }
    }
    this.localeSelect.value = current;
  }

  private fillControls(): void {
    const list = el<HTMLUListElement>('controls-list');
    list.replaceChildren();
    for (const key of CONTROL_KEYS) {
      const item = document.createElement('li');
      item.innerHTML = t(key);
      list.append(item);
    }
  }

  private fillControlsChrome(): void {
    setText('controls-kicker', t('controls.kicker'));
    setText('controls-title', t('controls.title'));
    setText('controls-close-hint', t('controls.closeHint'));
  }

  private fillGraphicsChrome(): void {
    setText('graphics-kicker', t('graphics.kicker'));
    setText('graphics-title', t('graphics.title'));
    setText('graphics-close-hint', t('graphics.closeHint'));
    setText('graphics-reset', t('graphics.reset'));
  }

  private fillInventoryChrome(): void {
    setText('inventory-kicker', t('inventory.kicker'));
    setText('inventory-title', t('inventory.title'));
    setText('inventory-close-hint', t('inventory.closeHint'));
    setText('inventory-instruction', t('inventory.instruction'));
    setText('inventory-selected-label', t('inventory.selected'));
    setText('inventory-loadout-title', t('inventory.hotbar'));
    setText('inventory-invert-scroll-label', t('inventory.invertHotbarScroll'));
    el<HTMLElement>('inventory-catalog-panel').setAttribute('aria-label', t('inventory.catalogAria'));
    el<HTMLElement>('inventory-tabs').setAttribute('aria-label', t('inventory.tablistAria'));
    for (const tab of this.inventoryTabs) {
      const id = tab.dataset.inventoryTab;
      if (!isInventoryTabId(id)) continue;
      tab.textContent = t(INVENTORY_TAB_KEYS[id]);
    }
  }

  private fillTextureCreatorCopy(): void {
    setText('texture-all-faces', t('inventory.allFaces'));
    setText('texture-name-label', t('inventory.name'));
    setText('texture-list-label', t('inventory.list'));
    setText('texture-list-new-label', t('inventory.newListName'));
    setText('texture-color-label', t('inventory.color'));
    setText('texture-pattern-label', t('inventory.pattern'));
    setText('texture-resolution-label', t('inventory.resolution'));
    setText('texture-noise-size-label', t('inventory.noiseScale'));
    setText('texture-noise-contrast-label', t('inventory.noiseContrast'));
    setText('texture-motif-label', t('inventory.motif'));
    setText('texture-reroll', t('inventory.reroll'));
    setText('texture-create-submit', t('inventory.create'));
    this.texturePreview.setAttribute('aria-label', t('inventory.previewAria'));
    this.textureListNew.placeholder = t('inventory.listPlaceholder');
    const jaDefault = messageCatalog('ja').inventory.newTexture;
    const enDefault = messageCatalog('en').inventory.newTexture;
    const currentDefault = t('inventory.newTexture');
    if (
      this.textureName.value.length === 0 ||
      this.textureName.value === currentDefault ||
      this.textureName.value === jaDefault ||
      this.textureName.value === enDefault
    ) {
      this.textureName.value = currentDefault;
    }
    this.fillTexturePatternOptions();
  }

  private fillTexturePatternOptions(): void {
    const selected = this.texturePattern.value;
    this.texturePattern.replaceChildren();
    for (const pattern of BLOCK_TEXTURE_PATTERNS) {
      const option = document.createElement('option');
      option.value = pattern;
      option.textContent = translate(`texturePattern.${pattern}`, undefined, pattern);
      this.texturePattern.append(option);
    }
    this.texturePattern.value = selected || 'outline';
  }

  /* ---------------------------------------------------------------- */
  /* 表示切り替え                                                      */
  /* ---------------------------------------------------------------- */

  setMode(mode: UiMode): void {
    if (mode === 'inventory' && prefersTouchPlay()) {
      mode = this.mode === 'playing' ? 'playing' : 'menu';
    }
    if (mode === 'inventory' && this.mode !== 'inventory' && this.builder) {
      this.editingSlot = this.builder.slot;
      this.renderInventoryHotbar(this.builder);
      this.renderSlotDetails(this.builder);
    }
    this.mode = mode;
    this.menu.hidden = mode !== 'menu';
    this.controlsMenu.hidden = mode !== 'controls';
    this.inventory.hidden = mode !== 'inventory';
    this.graphicsMenu.hidden = mode !== 'graphics';
    this.gameUi.hidden = mode !== 'playing';
    this.viewHint.hidden = mode !== 'viewing';
    if (mode !== 'playing') this.setPlayerListVisible(false);
    this.handlers.onModeChange(mode);
  }

  setStatus(status: ConnectionStatus): void {
    this.status = status;
    this.menuStatus.className = `menu-status status--${status}`;
    this.menuStatus.textContent =
      status === 'connecting'
        ? t('status.connecting')
        : status === 'open'
          ? t('status.open')
          : t('status.closed');
    this.renderPlayers(this.lastSelf, this.lastPeers);
  }

  setBootTiming(text: string | null): void {
    this.menuBoot.hidden = text === null;
    this.menuBoot.textContent = text ?? '';
  }

  setRoom(room: string): void {
    this.roomInput.value = room;
    this.playerlistRoom.textContent = room;
  }

  setName(name: string): void {
    this.playerName = name.trim() || 'anonymous';
    this.nameInput.value = name;
    if (!this.textureCreator.hidden) this.refreshTextureListOptions();
  }

  setSensitivity(value: number): void {
    this.sensitivity.value = String(value);
    this.sensitivityValue.textContent = value.toFixed(2);
  }

  setInvertHotbarScroll(enabled: boolean): void {
    this.invertHotbarScroll.checked = enabled;
  }

  setGraphicsQuality(id: GraphicsQualityId): void {
    this.graphicsQuality.value = id;
  }

  setDepthOfField(enabled: boolean): void {
    this.depthOfField.checked = enabled;
  }

  setSunSdfShadows(enabled: boolean): void {
    this.sunSdfShadows.checked = enabled;
  }

  setDiSdfShadows(enabled: boolean): void {
    this.diSdfShadows.checked = enabled;
  }

  setGiSdfShadows(enabled: boolean): void {
    this.giSdfShadows.checked = enabled;
  }

  setSdfAnalytic(enabled: boolean): void {
    this.sdfAnalytic.checked = enabled;
  }

  setSdfAnalyticBvh(enabled: boolean): void {
    this.sdfAnalyticBvh.checked = enabled;
  }

  setSdfVoxelSpheres(enabled: boolean): void {
    this.sdfVoxelSpheres.checked = enabled;
  }

  setSunlight(enabled: boolean): void {
    this.sunlight.checked = enabled;
  }

  setAmbient(enabled: boolean): void {
    this.ambientLight.checked = enabled;
  }

  setGiDiffuseGain(value: number): void {
    this.giDiffuseGain.value = String(value);
    this.giDiffuseGainValue.textContent = value.toFixed(1);
  }

  setGiDistanceAmbient(value: number): void {
    this.giDistanceAmbient.value = String(value);
    this.giDistanceAmbientValue.textContent = value.toFixed(2);
  }

  setGiNearAmbient(value: number): void {
    this.giNearAmbient.value = String(value);
    this.giNearAmbientValue.textContent = value.toFixed(2);
  }

  setDenoiser(mode: RestirDenoiserMode): void {
    this.denoiser.value = mode;
  }

  setRestirCheapVisibility(enabled: boolean): void {
    this.restirCheapVisibility.checked = enabled;
  }

  setCollisionDebug(enabled: boolean): void {
    this.collisionDebug.checked = enabled;
  }

  /** init_state / 作成通知で届いた、このルーム専用テクスチャの一覧を反映する。 */
  setBlockTextures(textures: Iterable<BlockTextureDefinition>): void {
    this.blockTextures.clear();
    this.texturePreviewUrls.clear();
    for (const texture of textures) this.blockTextures.set(texture.id, texture);
    this.renderTextureLibrary();
    if (this.builder) this.renderBuilder(this.builder);
  }

  /* ---------------------------------------------------------------- */
  /* ホットバー                                                        */
  /* ---------------------------------------------------------------- */

  renderBuilder(builder: Builder): void {
    this.builder = builder;
    this.renderHotbar(builder);
    if (this.mode !== 'inventory') return;
    this.renderInventoryHotbar(builder);
    this.renderSlotDetails(builder);
  }

  renderHotbar(builder: Builder): void {
    this.slots.forEach((slot, index) => {
      const config = builder.getSlotConfig(index);
      const presentation = this.slotPresentation(config);
      this.applySlotSwatch(slot, config);
      slot.title = presentation.title;
      const shape = slot.querySelector<HTMLSpanElement>('.slot-shape');
      if (shape) shape.textContent = presentation.glyph;
      slot.classList.toggle('active', index === builder.slot);
    });
    const held = builder.getSlotConfig(builder.slot);
    this.shapeLine.textContent =
      held.kind === 'empty'
        ? t('tool.empty')
        : held.kind === 'tool'
          ? toolSlotLabel(held)
          : held.kind === 'placeable'
            ? t('build.shapeLine', {
                shape: objectLabel(held.definitionId),
                summary: builder.shapeSummary(),
              })
            : held.kind === 'clipboard'
              ? t('build.shapeLine', {
                  shape: t('tool.clipboard'),
                  summary: builder.shapeSummary(),
                })
            : t('build.shapeLine', {
                shape: shapeLabel(held.shape.kind),
                summary: builder.shapeSummary(),
              });
  }

  private renderInventoryCatalogs(): void {
    const shapeCatalogs: Record<(typeof BUILD_SHAPES)[number], HTMLDivElement> = {
      brace: this.inventoryCatalogBrace,
      plane: this.inventoryCatalogPlane,
      polygon: this.inventoryCatalogPolygon,
      stair: this.inventoryCatalogStair,
    };
    for (const shape of BUILD_SHAPES) {
      const catalog = shapeCatalogs[shape];
      catalog.replaceChildren();
      const specialGrid = document.createElement('div');
      specialGrid.className = 'inventory-item-grid';
      specialGrid.append(
        this.createInventoryItem(createLightSlot(shape), t('inventory.light')),
        this.createInventoryItem(createWaterSlot(shape), t('inventory.water'))
      );
      catalog.append(specialGrid);
      for (const pack of TEXTURE_PACKS) {
        const texturedGroup = document.createElement('div');
        texturedGroup.className = 'inventory-pack-group';
        const heading = document.createElement('h3');
        heading.className = 'inventory-pack-heading';
        heading.textContent = packLabel(pack.id, pack.label);
        const texturedGrid = document.createElement('div');
        texturedGrid.className = 'inventory-item-grid';
        for (const material of pack.materials) {
          texturedGrid.append(
            this.createInventoryItem(
              createBuildSlot(material.id, shape),
              materialLabel(material.id, material.label)
            )
          );
        }
        texturedGroup.append(heading, texturedGrid);
        catalog.append(texturedGroup);
      }
    }

    this.inventoryCatalogObject.replaceChildren(
      ...OBJECT_DEFINITIONS.map((definition) =>
        this.createInventoryItem(createPlaceableSlot(definition.id), objectLabel(definition.id))
      )
    );

    this.inventoryCatalogTool.replaceChildren(
      this.createInventoryItem(createEmptySlot(), t('tool.empty')),
      this.createInventoryItem(createDestroyToolSlot(), t('tool.destroy')),
      this.createInventoryItem(createRangeDestroyToolSlot(), t('tool.rangeDestroy')),
      this.createInventoryItem(createCopyPasteToolSlot(), t('tool.copyPaste')),
      this.createInventoryItem(createBraceBendToolSlot(), t('tool.braceBend')),
      this.createInventoryItem(createExtrudeToolSlot(), t('tool.extrude')),
      this.createInventoryItem(createExtrudeUpToolSlot(), t('tool.extrudeUp')),
      this.createInventoryItem(createExtrudeDownToolSlot(), t('tool.extrudeDown')),
      this.createInventoryItem(createHoleToolSlot(), t('tool.hole')),
      this.createInventoryItem(createClothSimToolSlot(), t('tool.clothSim')),
      this.createInventoryItem(createRevolveToolSlot(), t('tool.revolve'))
    );
    this.renderTextureLibrary();
  }

  private buildInventory(): void {
    const loadout = document.createElement('div');
    loadout.className = 'inventory-hotbar-loadout';
    for (let index = 0; index < HOTBAR_SIZE; index++) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = index === EMPTY_SLOT_INDEX ? 'inventory-slot inventory-slot-hand' : 'inventory-slot';
      button.addEventListener('click', () => {
        this.editingSlot = index;
        if (this.builder) {
          this.renderInventoryHotbar(this.builder);
          this.renderSlotDetails(this.builder);
        }
      });
      if (index !== EMPTY_SLOT_INDEX) {
        button.addEventListener('dragover', (event) => {
          if (!this.draggedItem) return;
          event.preventDefault();
          if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
          button.classList.add('drop-target');
        });
        button.addEventListener('dragleave', (event) => {
          const next = event.relatedTarget;
          if (!(next instanceof Node) || !button.contains(next)) {
            button.classList.remove('drop-target');
          }
        });
        button.addEventListener('drop', (event) => {
          event.preventDefault();
          button.classList.remove('drop-target');
          if (!this.draggedItem) return;
          this.editingSlot = index;
          this.assignInventoryItem(index, this.draggedItem);
        });
      }
      if (index === EMPTY_SLOT_INDEX) {
        const frame = document.createElement('div');
        frame.className = 'inventory-hand';
        frame.append(button);
        this.inventoryHotbar.append(frame);
      } else {
        loadout.append(button);
      }
      this.inventorySlots.push(button);
    }
    this.inventoryHotbar.append(loadout);
  }

  private wireInventoryTabs(): void {
    for (const tab of this.inventoryTabs) {
      tab.addEventListener('click', () => {
        const id = tab.dataset.inventoryTab;
        if (!isInventoryTabId(id)) return;
        this.setInventoryTab(id);
      });
      tab.addEventListener('keydown', (event) => {
        const current = this.inventoryTabs.indexOf(tab);
        if (current < 0) return;
        let next = current;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          next = (current + 1) % this.inventoryTabs.length;
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          next = (current - 1 + this.inventoryTabs.length) % this.inventoryTabs.length;
        } else if (event.key === 'Home') {
          next = 0;
        } else if (event.key === 'End') {
          next = this.inventoryTabs.length - 1;
        } else {
          return;
        }
        event.preventDefault();
        const target = this.inventoryTabs[next];
        const id = target?.dataset.inventoryTab;
        if (!target || !isInventoryTabId(id)) return;
        this.setInventoryTab(id);
        target.focus();
      });
    }
    this.setInventoryTab(this.activeInventoryTab);
  }

  private setInventoryTab(tabId: InventoryTabId): void {
    this.activeInventoryTab = tabId;
    for (const tab of this.inventoryTabs) {
      const selected = tab.dataset.inventoryTab === tabId;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    }
    for (const panel of this.inventoryTabPanels) {
      panel.hidden = panel.dataset.inventoryTab !== tabId;
    }
    if (tabId === 'texture') {
      this.refreshTextureListOptions(this.personalDefaultList());
      this.renderTextureCreatorPreview();
      this.textureName.focus();
      this.textureName.select();
    }
  }

  private wireTextureCreator(): void {
    for (const pattern of BLOCK_TEXTURE_PATTERNS) {
      const option = document.createElement('option');
      option.value = pattern;
      option.textContent = translate(`texturePattern.${pattern}`, undefined, pattern);
      this.texturePattern.append(option);
    }
    this.texturePattern.value = 'outline';

    const updatePreview = () => this.renderTextureCreatorPreview();
    for (const control of [
      this.textureColor,
      this.texturePattern,
      this.textureResolution,
      this.textureNoiseSize,
      this.textureNoiseContrast,
    ]) {
      control.addEventListener('input', updatePreview);
      control.addEventListener('change', updatePreview);
    }

    this.textureList.addEventListener('change', () => this.syncTextureListNewField());
    el<HTMLButtonElement>('texture-reroll').addEventListener('click', () => {
      this.textureSeed = randomTextureSeed();
      this.renderTextureCreatorPreview();
    });
    this.textureCreator.addEventListener('submit', (event) => {
      event.preventDefault();
      const texture = this.textureFromCreator(`texture:${crypto.randomUUID()}`);
      if (!texture || !this.handlers.onTextureCreate(texture, this.editingSlot)) return;
      this.sessionTextureLists.add(texture.list);
      this.textureSeed = randomTextureSeed();
      this.textureName.value = t('inventory.newTexture');
      this.textureListNew.value = '';
      this.refreshTextureListOptions(texture.list);
      this.renderTextureCreatorPreview();
      this.setInventoryTab('plane');
    });

    this.refreshTextureListOptions();
    this.renderTextureCreatorPreview();
  }

  private personalDefaultList(): string {
    const name = this.playerName.replace(/[\u0000-\u001f\u007f]/g, '').trim() || 'anonymous';
    return t('texture.defaultList', { name });
  }

  private refreshTextureListOptions(preferred?: string): void {
    const personalDefault = this.personalDefaultList();
    const lists = new Set<string>([personalDefault, ...this.sessionTextureLists]);
    for (const texture of this.blockTextures.values()) {
      if (texture.list) lists.add(texture.list);
    }

    const ordered = [...lists].sort((a, b) => {
      if (a === personalDefault) return -1;
      if (b === personalDefault) return 1;
      if (a === UNCATEGORIZED_TEXTURE_LIST) return 1;
      if (b === UNCATEGORIZED_TEXTURE_LIST) return -1;
      return a.localeCompare(b, getLocale());
    });

    const previous = preferred ?? this.textureList.value;
    this.textureList.replaceChildren();
    for (const list of ordered) {
      const option = document.createElement('option');
      option.value = list;
      option.textContent = list;
      this.textureList.append(option);
    }
    const createOption = document.createElement('option');
    createOption.value = NEW_TEXTURE_LIST_VALUE;
    createOption.textContent = t('inventory.newList');
    this.textureList.append(createOption);

    if (previous === NEW_TEXTURE_LIST_VALUE || ordered.includes(previous)) {
      this.textureList.value = previous;
    } else {
      this.textureList.value = personalDefault;
    }
    this.syncTextureListNewField();
  }

  private syncTextureListNewField(): void {
    const creating = this.textureList.value === NEW_TEXTURE_LIST_VALUE;
    this.textureListNewField.hidden = !creating;
    this.textureListNew.required = creating;
    if (creating) this.textureListNew.focus();
  }

  private selectedTextureList(): string | null {
    if (this.textureList.value === NEW_TEXTURE_LIST_VALUE) {
      const list = this.textureListNew.value.replace(/[\u0000-\u001f\u007f]/g, '').trim();
      if (list.length === 0 || list.length > MAX_TEXTURE_LIST_LENGTH) return null;
      return list;
    }
    const list = this.textureList.value.trim();
    return list.length > 0 ? list : null;
  }

  private textureFromCreator(id: string): BlockTextureDefinition | null {
    const color = Number.parseInt(this.textureColor.value.slice(1), 16);
    const resolution = Number(this.textureResolution.value) as BlockTextureResolution;
    const noiseSize = Number(this.textureNoiseSize.value);
    const noiseContrast = Number(this.textureNoiseContrast.value) / 100;
    const name = this.textureName.value.trim();
    const list = this.selectedTextureList() ?? this.personalDefaultList();
    if (
      name.length === 0 ||
      !Number.isFinite(color) ||
      !Number.isFinite(noiseSize) ||
      !Number.isFinite(noiseContrast)
    ) {
      return null;
    }
    if (this.textureList.value === NEW_TEXTURE_LIST_VALUE && this.selectedTextureList() === null) {
      return null;
    }
    return {
      id,
      name,
      list,
      color,
      pattern: this.texturePattern.value as BlockTexturePattern,
      resolution,
      noiseSize,
      noiseContrast,
      seed: this.textureSeed,
    };
  }

  private renderTextureCreatorPreview(): void {
    this.textureNoiseSizeValue.textContent = `${this.textureNoiseSize.value}px`;
    this.textureNoiseContrastValue.textContent = `${this.textureNoiseContrast.value}%`;
    const texture = this.textureFromCreator('texture:preview');
    if (texture) paintBlockTexture(this.texturePreview, texture);
  }

  private renderTextureLibrary(): void {
    const shapeCatalogs: Record<(typeof BUILD_SHAPES)[number], HTMLDivElement> = {
      brace: this.inventoryCatalogBrace,
      plane: this.inventoryCatalogPlane,
      polygon: this.inventoryCatalogPolygon,
      stair: this.inventoryCatalogStair,
    };
    const grouped = this.groupedBlockTextures();
    for (const shape of BUILD_SHAPES) {
      const catalog = shapeCatalogs[shape];
      const panel = catalog.parentElement;
      if (!(panel instanceof HTMLElement)) continue;
      // ルーム同期で作り直すのは自作テクスチャだけ。パック素材は catalog 側に残す。
      for (const node of [...panel.children]) {
        if (node.classList.contains('texture-library-group')) node.remove();
      }
      for (const [listName, textures] of grouped) {
        const group = document.createElement('section');
        group.className = 'texture-library-group';
        const heading = document.createElement('h3');
        heading.className = 'texture-library-group-heading';
        heading.textContent = listName;
        const grid = document.createElement('div');
        grid.className = 'inventory-item-grid';
        for (const texture of textures) {
          const item = this.createInventoryItem(createBuildSlot(texture.id, shape), texture.name);
          item.classList.add('texture-library-item');
          grid.append(item);
        }
        group.append(heading, grid);
        panel.append(group);
      }
    }
    if (!this.textureCreator.hidden) this.refreshTextureListOptions();
  }

  private groupedBlockTextures(): Array<[string, BlockTextureDefinition[]]> {
    const groups = new Map<string, BlockTextureDefinition[]>();
    for (const texture of this.blockTextures.values()) {
      const list = texture.list || UNCATEGORIZED_TEXTURE_LIST;
      const entries = groups.get(list);
      if (entries) entries.push(texture);
      else groups.set(list, [texture]);
    }
    const personalDefault = this.personalDefaultList();
    return [...groups.entries()].sort(([a], [b]) => {
      if (a === personalDefault) return -1;
      if (b === personalDefault) return 1;
      if (a === UNCATEGORIZED_TEXTURE_LIST) return 1;
      if (b === UNCATEGORIZED_TEXTURE_LIST) return -1;
      return a.localeCompare(b, getLocale());
    });
  }

  private materialPresentation(id: string): { label: string; color: number } {
    const custom = this.blockTextures.get(id);
    if (custom) return { label: custom.name, color: custom.color };
    const fixed = resolveMaterial(id);
    return { label: materialLabel(id, fixed.label), color: fixed.color };
  }

  private slotPresentation(config: SlotConfig): { label: string; glyph: string; title: string } {
    if (config.kind === 'empty') {
      const label = t('tool.empty');
      return { label, glyph: '', title: label };
    }
    if (config.kind === 'tool') {
      const label = toolSlotLabel(config);
      return { label, glyph: TOOL_GLYPHS[config.tool], title: label };
    }
    if (config.kind === 'placeable') {
      const label = objectLabel(config.definitionId);
      return {
        label,
        glyph: SHAPE_GLYPHS.object,
        title: t('inventory.materialTitle', {
          material: label,
          shape: t('shape.object'),
        }),
      };
    }
    if (config.kind === 'clipboard') {
      const label = t('tool.clipboardCount', { count: config.items.length });
      return { label, glyph: '⧉', title: label };
    }
    const material = this.materialPresentation(config.material);
    return {
      label: material.label,
      glyph: SHAPE_GLYPHS[config.shape.kind],
      title: t('inventory.materialTitle', {
        material: material.label,
        shape: shapeLabel(config.shape.kind),
      }),
    };
  }

  private applySlotSwatch(element: HTMLElement, config: SlotConfig): void {
    if (config.kind === 'empty') {
      element.style.backgroundColor = colorCss(0x16191d);
      element.style.backgroundImage = '';
      element.style.backgroundSize = '';
      element.style.imageRendering = '';
      return;
    }
    if (config.kind === 'tool') {
      element.style.backgroundColor = colorCss(TOOL_SWATCH_COLORS[config.tool]);
      element.style.backgroundImage = '';
      element.style.backgroundSize = '';
      element.style.imageRendering = '';
      return;
    }
    if (config.kind === 'clipboard') {
      element.style.backgroundColor = colorCss(0x3a5a7a);
      element.style.backgroundImage = '';
      element.style.backgroundSize = '';
      element.style.imageRendering = '';
      return;
    }
    if (config.kind === 'placeable') {
      this.applyMaterialSwatch(
        element,
        config.material ?? OBJECT_DEFINITIONS.find((item) => item.id === config.definitionId)?.material ?? 'default'
      );
      return;
    }
    this.applyMaterialSwatch(element, config.material);
  }

  private applyMaterialSwatch(element: HTMLElement, materialId: string): void {
    const material = this.materialPresentation(materialId);
    const custom = this.blockTextures.get(materialId);
    const preview = custom
      ? this.texturePreviewUrl(custom)
      : materialPreviewUrl(resolveMaterial(materialId));
    element.style.backgroundColor = colorCss(material.color);
    element.style.backgroundImage = preview ? `url("${preview}")` : '';
    element.style.backgroundSize = 'cover';
    element.style.imageRendering = custom ? 'pixelated' : '';
  }

  private texturePreviewUrl(texture: BlockTextureDefinition): string {
    const cached = this.texturePreviewUrls.get(texture.id);
    if (cached) return cached;
    const url = blockTextureDataUrl(texture);
    this.texturePreviewUrls.set(texture.id, url);
    return url;
  }

  private createInventoryItem(item: SlotConfig, label: string): HTMLButtonElement {
    const presentation = this.slotPresentation(item);
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'inventory-item';
    button.draggable = true;
    button.title = presentation.title;
    button.setAttribute('aria-label', t('inventory.dragAria', { label }));

    const swatch = document.createElement('span');
    swatch.className = 'inventory-item-swatch';
    this.applySlotSwatch(swatch, item);
    const glyph = document.createElement('span');
    glyph.className = 'inventory-item-glyph';
    glyph.textContent = presentation.glyph;
    const name = document.createElement('span');
    name.className = 'inventory-item-name';
    name.textContent = label;
    button.append(swatch, glyph, name);

    button.addEventListener('dragstart', (event) => {
      this.draggedItem = item;
      button.classList.add('dragging');
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'copy';
        event.dataTransfer.setData('text/plain', JSON.stringify(item));
      }
    });
    button.addEventListener('dragend', () => {
      this.draggedItem = null;
      button.classList.remove('dragging');
      this.inventorySlots.forEach((slot) => slot.classList.remove('drop-target'));
    });
    return button;
  }

  private assignInventoryItem(index: number, item: SlotConfig): void {
    if (index === EMPTY_SLOT_INDEX) return;
    this.handlers.onSlotChange(index, item);
  }

  private renderInventoryHotbar(builder: Builder): void {
    this.inventorySlots.forEach((button, index) => {
      const config = builder.getSlotConfig(index);
      const presentation = this.slotPresentation(config);
      button.classList.toggle('selected', index === this.editingSlot);
      button.setAttribute('aria-pressed', String(index === this.editingSlot));
      button.title = t('inventory.slotTitle', { n: index, title: presentation.title });

      const number = document.createElement('span');
      number.className = 'inventory-slot-number';
      number.textContent = String(index);
      const swatch = document.createElement('span');
      swatch.className = 'inventory-slot-swatch';
      this.applySlotSwatch(swatch, config);
      const glyph = document.createElement('span');
      glyph.className = 'inventory-slot-glyph';
      glyph.textContent = presentation.glyph;
      const caption = document.createElement('span');
      caption.className = 'inventory-slot-caption';
      caption.textContent = presentation.label;
      button.replaceChildren(number, swatch, glyph, caption);
    });
  }

  private renderSlotDetails(builder: Builder): void {
    const index = this.editingSlot;
    const config = builder.getSlotConfig(index);
    const presentation = this.slotPresentation(config);
    this.inventoryInspectorTitle.textContent = t('inventory.slot', { n: index });

    const current = document.createElement('div');
    current.className = 'inventory-current-item';
    const swatch = document.createElement('span');
    swatch.className = 'inventory-current-swatch';
    this.applySlotSwatch(swatch, config);
    swatch.textContent = presentation.glyph;
    const description = document.createElement('span');
    const name = document.createElement('strong');
    name.textContent = presentation.label;
    const shape = document.createElement('small');
    shape.textContent =
      config.kind === 'empty'
        ? t('inventory.empty')
        : config.kind === 'tool'
          ? t('inventory.tool')
          : config.kind === 'placeable'
            ? t('shape.object')
            : config.kind === 'clipboard'
              ? t('tool.clipboard')
            : shapeLabel(config.shape.kind);
    description.append(name, shape);
    current.append(swatch, description);

    const details = document.createElement('div');
    details.className = 'inventory-parameter-grid';
    if (config.kind === 'empty') {
      const note = document.createElement('p');
      note.className = 'inventory-tool-note';
      note.textContent = t('inventory.emptyNote');
      details.append(note);
      this.inventorySlotDetails.replaceChildren(current, details);
      return;
    }
    if (config.kind === 'tool') {
      const note = document.createElement('p');
      note.className = 'inventory-tool-note';
      note.textContent =
        config.tool === 'range-destroy'
          ? t('inventory.rangeDestroyNote')
          : config.tool === 'copy-paste'
            ? t('inventory.copyPasteNote')
          : config.tool === 'brace-bend'
            ? t('inventory.braceBendNote')
            : config.tool === 'extrude'
              ? t('inventory.extrudeNote')
              : config.tool === 'extrude-up'
                ? t('inventory.extrudeUpNote')
                : config.tool === 'extrude-down'
                  ? t('inventory.extrudeDownNote')
                  : config.tool === 'hole'
                    ? t('inventory.holeNote')
                    : config.tool === 'cloth-sim'
                      ? t('inventory.clothSimNote')
                      : config.tool === 'revolve'
                        ? t('inventory.revolveNote')
                    : t('inventory.destroyNote');
      details.append(note);
      this.inventorySlotDetails.replaceChildren(current, details);
      return;
    }

    if (config.kind === 'placeable') {
      const note = document.createElement('p');
      note.className = 'inventory-tool-note';
      note.textContent = t('inventory.objectNote');
      details.append(note);
      this.inventorySlotDetails.replaceChildren(current, details);
      return;
    }

    if (config.kind === 'clipboard') {
      const note = document.createElement('p');
      note.className = 'inventory-tool-note';
      note.textContent = t('inventory.clipboardNote');
      details.append(note);
      this.inventorySlotDetails.replaceChildren(current, details);
      return;
    }

    if (config.shape.kind === 'brace') {
      const brace = config.shape;
      details.append(
        createNumberField(t('inventory.thickness'), brace.thickness, 0.05, 4, 0.05, (value) => {
          this.handlers.onSlotChange(index, {
            ...config,
            shape: { ...brace, thickness: value },
          });
        }),
        createNumberField(t('inventory.rotation'), brace.rotation, -180, 180, 1, (value) => {
          this.handlers.onSlotChange(index, {
            ...config,
            shape: { ...brace, rotation: value },
          });
        })
      );
    } else if (config.shape.kind === 'plane' || config.shape.kind === 'polygon') {
      const plane = config.shape;
      details.append(
        createNumberField(t('inventory.thickness'), plane.thickness, 0, 4, 0.05, (value) => {
          this.handlers.onSlotChange(index, {
            ...config,
            shape: { ...plane, thickness: value },
          });
        })
      );
    }

    this.inventorySlotDetails.replaceChildren(current, details);
  }

  /** 持ち替えたときにマイクラのようにアイテム名を出す */
  showItemName(text: string): void {
    this.itemName.textContent = text;
    this.itemName.classList.add('show');
    window.clearTimeout(this.itemNameTimer);
    this.itemNameTimer = window.setTimeout(() => {
      this.itemName.classList.remove('show');
    }, 1600);
  }

  /* ---------------------------------------------------------------- */
  /* デバッグ / 参加者一覧 / トースト                                  */
  /* ---------------------------------------------------------------- */

  setFps(fps: number): void {
    this.fps.textContent = `FPS ${fps.toFixed(0)}`;
  }

  toggleDebug(): void {
    this.debug.hidden = !this.debug.hidden;
  }

  get debugVisible(): boolean {
    return !this.debug.hidden;
  }

  setDebugText(text: string): void {
    this.debug.textContent = text;
  }

  setPlayerListVisible(visible: boolean): void {
    this.playerlist.hidden = !visible;
  }

  renderPlayers(self: PeerInfo | null, peers: Iterable<PeerInfo>): void {
    this.lastSelf = self;
    this.lastPeers = [...peers];
    const all = self ? [self, ...this.lastPeers] : this.lastPeers;
    this.fillPlayerList(this.playerlistItems, self, all);

    const ready = this.status === 'open' && all.length > 0;
    this.menuPlayersHeading.textContent = t('playerlist.menuHeading', {
      label: t('menu.players'),
      count: formatLocaleNumber(ready ? all.length : 0),
    });
    this.menuPlayerlistItems.replaceChildren();
    this.menuPlayersEmpty.hidden = ready;
    if (ready) {
      this.fillPlayerList(this.menuPlayerlistItems, self, all);
    } else {
      this.menuPlayersEmpty.textContent =
        this.status === 'closed' ? t('playerlist.emptyClosed') : t('playerlist.emptyConnecting');
    }
  }

  private fillPlayerList(list: HTMLUListElement, self: PeerInfo | null, peers: PeerInfo[]): void {
    list.replaceChildren();
    for (const peer of peers) {
      const item = document.createElement('li');
      const dot = document.createElement('span');
      dot.className = 'dot';
      dot.style.background = peer.color;
      const label = document.createElement('span');
      const isSelf = self !== null && peer.playerId === self.playerId;
      if (isSelf) label.className = 'self';
      label.textContent = isSelf ? t('playerlist.self', { name: peer.name }) : peer.name;
      item.append(dot, label);
      list.append(item);
    }
  }

  toast(message: string, kind: 'info' | 'error' = 'info'): void {
    const node = document.createElement('div');
    node.className = `toast toast--${kind}`;
    node.textContent = message;
    this.toasts.append(node);
    window.setTimeout(() => node.remove(), 3200);
  }
}

function setText(id: string, text: string): void {
  el<HTMLElement>(id).textContent = text;
}

function shapeLabel(kind: 'brace' | 'plane' | 'polygon' | 'stair'): string {
  if (kind === 'brace') return t('shape.brace');
  if (kind === 'stair') return t('shape.stair');
  if (kind === 'polygon') return t('shape.polygon');
  return t('shape.plane');
}

function createField(labelText: string, control: HTMLElement): HTMLLabelElement {
  const field = document.createElement('label');
  field.className = 'slot-editor-field';
  const label = document.createElement('span');
  label.textContent = labelText;
  field.append(label, control);
  return field;
}

function colorCss(color: number): string {
  return `#${color.toString(16).padStart(6, '0')}`;
}

function randomTextureSeed(): number {
  return crypto.getRandomValues(new Uint32Array(1))[0] & 0x7fffffff;
}

function createNumberField(
  label: string,
  value: number,
  min: number,
  max: number,
  step: number,
  onChange: (value: number) => void
): HTMLLabelElement {
  const input = document.createElement('input');
  input.type = 'number';
  input.value = String(value);
  input.min = String(min);
  input.max = String(max);
  input.step = String(step);
  input.addEventListener('change', () => {
    const next = Number(input.value);
    if (Number.isFinite(next)) onChange(next);
  });
  return createField(label, input);
}
